import { useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service.ts";
import React, { useMemo, useState } from "react";
import { selectOpenTradesFormattedNumTradesOverTimeData } from "features/trades/TradesCharts/NumTradesOverTime/redux/numTradesOverTimeSlice.js";
import BarChart from "common/Charts/BarChart.tsx";
import Title from "common/Typography/Title.tsx";
import ChartCardLayout from "common/Layouts/ChartCardLayout.jsx";
import styled, { useTheme } from "styled-components";
import Card from "common/Boxes/Card.tsx";
import {
  FormattedChartDataPoint,
  LanguageNameSpaces,
  TimeRange,
} from "@shared/types";
import { TIME_RANGE_1_WEEK } from "@shared/common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import NewLookChartCard from "features/trades/NewLook/NewLookChartCard.tsx";

interface NumOfTradesChartProps {
  /** New look: title row and chart only, for the overview panel. */
  flush?: boolean;
}

const NumOfTradesChart = ({ flush }: NumOfTradesChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { isNewLook } = useTheme();
  const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGE_1_WEEK);

  const { isFetching, isSuccess } =
    sharedLib.sharedApi.trades.useGetNumberOfTradesGraphQuery(
      { time_range: timeRange },
      { refetchOnMountOrArgChange: true }
    );

  const numTradesSelector = useMemo(
    () => selectOpenTradesFormattedNumTradesOverTimeData(timeRange),
    [timeRange]
  );
  const data: FormattedChartDataPoint[] = useSelector(numTradesSelector);

  const chart = (
    <BarChart
      data={data}
      loading={isFetching}
      isSuccess={isSuccess}
      precision={0}
    />
  );

  if (isNewLook) {
    return (
      <NewLookChartCard
        title={t("charts.number_of_trades")}
        chart={chart}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        flush={flush}
      />
    );
  }

  return (
    <ChartCard>
      <ChartCardLayout
        change={
          <Title type="secondary" level={3}>
            {t("charts.number_of_trades")}
          </Title>
        }
        chart={chart}
        selectedTimeRange={timeRange}
        setTimeRange={setTimeRange}
        autoLayoutHeight={true}
        buttonSize="medium"
      />
    </ChartCard>
  );
};

export default NumOfTradesChart;

const ChartCard = styled(Card)`
  overflow: hidden;
  height: 100%;
`;
